// Weather Dashboard Constants and Helpers

// Growth stage definitions
export const CORN_GROWTH_STAGES = ["Emergence", "V6", "V12", "Tasseling", "R1", "R6"];
export const SOYBEAN_GROWTH_STAGES = ["Emergence", "V3", "R1", "R3", "R5", "R8"];
export const SORGHUM_GROWTH_STAGES = ["Emergence", "V6", "Boot", "Flowering", "R6"];
export const COTTON_GROWTH_STAGES = ["Emergence", "First Square", "First Bloom", "Peak Bloom", "Open Boll", "Maturity"];
export const WHEAT_GROWTH_STAGES = [
  "Emergence",
  "Tillering",
  "Jointing",
  "Boot",
  "Heading",
  "Flowering",
  "Milk",
  "Dough",
  "Maturity",
];
export const RICE_GROWTH_STAGES = ["Emergence", "Tillering", "Panicle Initiation", "Booting", "Heading", "Maturity"];

// Growth stage explanations
export const CORN_GROWTH_STAGE_EXPLANATIONS = [
  "Coleoptile reaches soil surface, growing point remains below soil for protection",
  "Growing point moves above soil surface, rapid growth begins, ear shoots initiate",
  "Kernel row determination nearly complete, critical period for yield begins",
  "Last tassel branch visible, plant reaches full height, pollen shed begins",
  "Silks emerge from husks, pollination occurs, most vulnerable to stress",
  "Physiological maturity reached, kernels at maximum dry weight, black layer forms",
];

export const SOYBEAN_GROWTH_STAGE_EXPLANATIONS = [
  "Cotyledons have been pulled through the soil surface, plant emerges from ground",
  "Third trifoliolate leaf fully developed, plant 7-9 inches tall with active nitrogen fixation beginning",
  "Beginning bloom stage with at least one open flower on any node of the main stem",
  "Beginning pod development with pods 3/16 inch long at uppermost nodes, critical yield period begins",
  "Beginning seed formation with seeds 1/8 inch long, requires maximum water and nutrients",
  "Full maturity reached with 95% of pods at mature color, ready for harvest preparation",
];

export const SORGHUM_GROWTH_STAGE_EXPLANATIONS = [
  "Plant breaks through soil surface, early growth is slow and depends on seed reserves",
  "Growing point differentiation occurs, changes from leaf to head production, rapid growth begins",
  "Head develops to nearly full size and is enclosed in flag-leaf sheath, maximum leaf area achieved",
  "Half-bloom with head fully extended, pollination occurs, grain formation begins",
  "Physiological maturity reached, grains achieve maximum dry weight with black layer formation",
];

export const COTTON_GROWTH_STAGE_EXPLANATIONS = [
  "Seedling emerges from soil, cotyledons unfold, stand establishment begins",
  "First flower bud (square) visible, plant enters reproductive phase",
  "First flower opens, beginning of bloom period, critical for yield determination",
  "Maximum number of flowers open, peak flowering, boll development accelerates",
  "First bolls open, fiber development completes, harvest approaches",
  "All harvestable bolls are open, crop reaches physiological maturity",
];

export const WHEAT_GROWTH_STAGE_EXPLANATIONS = [
  "Seedling emerges from soil, coleoptile visible above ground",
  "Multiple shoots (tillers) develop from the base of the plant",
  "Stem elongation begins, nodes become visible as plant grows taller",
  "Flag leaf sheath swells as head develops inside",
  "Head emerges fully from the flag leaf sheath",
  "Flowering occurs, pollination and fertilization take place",
  "Grain enters milk stage, kernels filled with milky fluid",
  "Grain enters dough stage, kernels start to harden",
  "Physiological maturity reached, kernels hard and dry, ready for harvest",
];

export const RICE_GROWTH_STAGE_EXPLANATIONS = [
  "Seedling emerges from soil; coleoptile and first leaf visible above ground",
  "Active tiller production; plant increases in number of stems, rapid vegetative growth",
  "Initiation of panicle (flower cluster) within the stem; reproductive development begins",
  "Panicle develops and enlarges within flag leaf sheath; stem elongation peaks",
  "Panicle (head) fully emerges from sheath; flowering and pollination occur",
  "Grain filling completes; physiological maturity reached, grains harden and lose green color",
];

// GDD benchmarks for growth stages
export const CORN_GDD_BENCHMARKS = [0, 200, 600, 1200, 1400, 2500];
export const SOYBEAN_GDD_BENCHMARKS = [0, 150, 450, 900, 1400, 2200];
export const SORGHUM_GDD_BENCHMARKS = [0, 200, 800, 1100, 1400, 2200];
export const COTTON_GDD_BENCHMARKS = [0, 300, 600, 900, 1200, 1800];
export const WHEAT_GDD_BENCHMARKS = [0, 150, 400, 700, 900, 1100, 1300, 1500, 1800];
export const RICE_GDD_BENCHMARKS = [0, 250, 700, 1000, 1250, 1800];

// Weather API constants
export const WEATHER_OBJECT_NAME = "Weather";
export const WEATHER_NAMESPACE = "Gridmet";
export const DEFAULT_YEAR = new Date().getFullYear();

// Chart type constants
export const CHART_TYPE_TIMESERIES = "timeseries";
export const CHART_TYPE_BAR = "bar";
export const CHART_TYPE_LINE = "line";

// Weather data field mappings
export const WEATHER_FIELDS = {
  DATE: "date",
  MAX_TEMP: "mean_maximum_air_temperature_fahrenheit",
  MIN_TEMP: "mean_minimum_air_temperature_fahrenheit",
  MAX_TEMP_PRIOR: "mean_maximum_air_temperature_fahrenheit_prior",
  MIN_TEMP_PRIOR: "mean_minimum_air_temperature_fahrenheit_prior",

  PRECIPITATION: "precipitation_millimeters",
  PRECIPITATION_PRIOR: "precipitation_millimeters_prior",

  WIND_SPEED: "mean_wind_speed",

  GDD_CORN: "sum_gdd_corn",
  GDD_SOYBEANS: "sum_gdd_soybeans",
  GDD_SORGHUM: "sum_gdd_sorghum",
  GDD_WHEAT: "sum_gdd_wheat",
  GDD_COTTON: "sum_gdd_cotton",
  GDD_RICE: "sum_gdd_rice",

  GDD_CORN_PRIOR: "sum_gdd_corn_prior",
  GDD_SOYBEANS_PRIOR: "sum_gdd_soybeans_prior",
  GDD_SORGHUM_PRIOR: "sum_gdd_sorghum_prior",
  GDD_WHEAT_PRIOR: "sum_gdd_wheat_prior",
  GDD_COTTON_PRIOR: "sum_gdd_cotton_prior",
  GDD_RICE_PRIOR: "sum_gdd_rice_prior",

  PDSI: "mean_pdsi",
  PDSI_PRIOR: "mean_pdsi_prior",

  Z_INDEX: "mean_z",
  Z_INDEX_PRIOR: "mean_z_prior",
};

// Drought index ranges for mean Z index classification
export const DROUGHT_RANGES = [
  {
    label: "Extremely Wet",
    min: 4.0,
    max: 9999,
  },
  {
    label: "Very Wet",
    min: 3.0,
    max: 4.0,
  },
  {
    label: "Moderately Wet",
    min: 2.0,
    max: 3.0,
  },
  {
    label: "Slightly Wet",
    min: 1.0,
    max: 2.0,
  },
  {
    label: "Near Normal",
    min: -0.5,
    max: 1.0,
  },
  {
    label: "Incipient Dry Spell",
    min: -1.0,
    max: -0.5,
  },
  {
    label: "Mild Drought",
    min: -2.0,
    max: -1.0,
  },
  {
    label: "Moderate Drought",
    min: -3.0,
    max: -2.0,
  },
  {
    label: "Severe Drought",
    min: -4.0,
    max: -3.0,
  },
  {
    label: "Extreme Drought",
    min: -9999,
    max: -4.0,
  },
];

// Color schemes for charts
export const CHART_COLORS = {
  TEMPERATURE: {
    MAX: "#E74C3C", // Red for max temperature
    MIN: "#2980B9", // Blue for min temperature
  },
  PRECIPITATION: "#8E44AD", // Purple
  WIND: "#1B4F72", // Dark blue
  GDD: "#27AE60", // Green
  DROUGHT: {
    PDSI: "#E74C3C", // Red
    Z_INDEX: "#95A5A6", // Grey
  },

  PRIOR: "#c0c0c088",
};

// Helper functions
export const getGrowthStages = (cropType) => {
  switch (cropType?.toLowerCase()) {
    case "corn":
      return CORN_GROWTH_STAGES;
    case "soybeans":
    case "soybean":
      return SOYBEAN_GROWTH_STAGES;
    case "sorghum":
      return SORGHUM_GROWTH_STAGES;
    case "cotton":
      return COTTON_GROWTH_STAGES;
    case "wheat":
      return WHEAT_GROWTH_STAGES;
    case "rice":
      return RICE_GROWTH_STAGES;
    default:
      return CORN_GROWTH_STAGES;
  }
};

export const getGrowthStageExplanations = (cropType) => {
  switch (cropType?.toLowerCase()) {
    case "corn":
      return CORN_GROWTH_STAGE_EXPLANATIONS;
    case "soybeans":
    case "soybean":
      return SOYBEAN_GROWTH_STAGE_EXPLANATIONS;
    case "sorghum":
      return SORGHUM_GROWTH_STAGE_EXPLANATIONS;
    case "cotton":
      return COTTON_GROWTH_STAGE_EXPLANATIONS;
    case "wheat":
      return WHEAT_GROWTH_STAGE_EXPLANATIONS;
    case "rice":
      return RICE_GROWTH_STAGE_EXPLANATIONS;
    default:
      return CORN_GROWTH_STAGE_EXPLANATIONS;
  }
};

export const getGDDBenchmarks = (cropType) => {
  switch (cropType?.toLowerCase()) {
    case "corn":
      return CORN_GDD_BENCHMARKS;
    case "soybeans":
    case "soybean":
      return SOYBEAN_GDD_BENCHMARKS;
    case "sorghum":
      return SORGHUM_GDD_BENCHMARKS;
    case "cotton":
      return COTTON_GDD_BENCHMARKS;
    case "wheat":
      return WHEAT_GDD_BENCHMARKS;
    case "rice":
      return RICE_GDD_BENCHMARKS;
    default:
      return CORN_GDD_BENCHMARKS;
  }
};

export const getGDDFieldName = (cropType) => {
  switch (cropType?.toLowerCase()) {
    case "corn":
      return WEATHER_FIELDS.GDD_CORN;
    case "soybeans":
    case "soybean":
      return WEATHER_FIELDS.GDD_SOYBEANS;
    case "sorghum":
      return WEATHER_FIELDS.GDD_SORGHUM;
    case "wheat":
      return WEATHER_FIELDS.GDD_WHEAT;
    case "cotton":
      return WEATHER_FIELDS.GDD_COTTON;
    case "rice":
      return WEATHER_FIELDS.GDD_RICE;
    default:
      return WEATHER_FIELDS.GDD_CORN;
  }
};

// Format weather data for charts
export const formatWeatherDataForChart = (weatherData, fieldNameY, dateRange, priorYearData = null) => {
  if (!weatherData?.length) {
    return [];
  }

  let filteredData = weatherData;

  // Apply date range filter if provided
  if (dateRange?.start && dateRange?.end) {
    filteredData = weatherData.filter((item) => {
      const itemDate = new Date(item[WEATHER_FIELDS.DATE]);
      const isInRange = itemDate >= dateRange.start && itemDate <= dateRange.end;
      return isInRange;
    });

    // If we have prior year data and current year filtered data is empty or incomplete,
    // try to use prior year data to fill gaps
    if (priorYearData?.length && filteredData.length < weatherData.length) {
      const priorYearFiltered = priorYearData.filter((item) => {
        // Convert prior year date to current year equivalent for range checking
        const originalDate = new Date(item[WEATHER_FIELDS.DATE]);
        const currentYearEquivalent = new Date(
          dateRange.start.getFullYear(),
          originalDate.getMonth(),
          originalDate.getDate()
        );
        const isInRange = currentYearEquivalent >= dateRange.start && currentYearEquivalent <= dateRange.end;
        return isInRange;
      });

      // Add prior year data for dates where current year data is missing
      priorYearFiltered.forEach((priorItem) => {
        const originalDate = new Date(priorItem[WEATHER_FIELDS.DATE]);
        const currentYearEquivalent = new Date(
          dateRange.start.getFullYear(),
          originalDate.getMonth(),
          originalDate.getDate()
        );

        // Check if we already have data for this date in current year
        const existsInCurrent = filteredData.some((currentItem) => {
          const currentDate = new Date(currentItem[WEATHER_FIELDS.DATE]);
          return currentDate.getTime() === currentYearEquivalent.getTime();
        });

        // If not exists in current year, add the prior year data with adjusted date
        if (!existsInCurrent) {
          filteredData.push({
            ...priorItem,
            [WEATHER_FIELDS.DATE]: currentYearEquivalent.toISOString().split("T")[0],
          });
        }
      });

      // Sort by date to maintain chronological order
      filteredData.sort((a, b) => new Date(a[WEATHER_FIELDS.DATE]) - new Date(b[WEATHER_FIELDS.DATE]));
    }
  }

  const result = filteredData.map((item) => ({
    ...item,
    county_name: "",
    state_name: "",
    [WEATHER_FIELDS.DATE]: item[WEATHER_FIELDS.DATE],
    [fieldNameY]: item[fieldNameY],
  }));
  return result;
};

// Calculate cumulative values (for GDD)
export const calculateCumulative = (data, fieldName) => {
  if (!data?.length) return [];

  let cumulative = 0;
  let cumulativePrior = 0;
  return data.map((item) => {
    cumulative += item[fieldName] || 0;
    cumulativePrior += item[`${fieldName}_prior`] || 0;
    return {
      ...item,
      [`cumulative_${fieldName}`]: cumulative,
      [`cumulative_${fieldName}_prior`]: cumulativePrior,
    };
  });
};

// Get drought severity label based on mean Z index value
export const getDroughtSeverityLabel = (meanZIndexValue) => {
  if (meanZIndexValue === null || meanZIndexValue === undefined) return "N/A";

  // Find the range that contains the meanZIndexValue
  const matchingRange = DROUGHT_RANGES.find((range) => meanZIndexValue >= range.min && meanZIndexValue < range.max);

  return matchingRange ? matchingRange.label : "N/A";
};

// Calculate total precipitation
export const calculateTotalPrecipitation = (weatherData) => {
  if (!weatherData?.length) return 0;
  const precipitationMillimeters = weatherData.reduce(
    (total, item) => total + (item[WEATHER_FIELDS.PRECIPITATION] || 0),
    0
  );
  return precipitationMillimeters;
};

// Calculate average temperature
export const calculateAverageTemperature = (weatherData) => {
  if (!weatherData?.length) return { max: 0, min: 0 };

  const totals = weatherData.reduce(
    (acc, item) => ({
      max: acc.max + (item[WEATHER_FIELDS.MAX_TEMP] || 0),
      min: acc.min + (item[WEATHER_FIELDS.MIN_TEMP] || 0),
    }),
    { max: 0, min: 0 }
  );

  const returnValue = {
    max: totals.max / weatherData.length,
    min: totals.min / weatherData.length,
  };

  return returnValue;
};

// Get current growth stage based on GDD
export const getCurrentGrowthStage = (currentGDD, cropType) => {
  const stages = getGrowthStages(cropType);
  const benchmarks = getGDDBenchmarks(cropType);

  // Find the current stage based on GDD
  for (let i = benchmarks.length - 1; i >= 0; i--) {
    if (currentGDD >= benchmarks[i]) {
      let stageProgress = 0;

      // Calculate progress within the current stage
      if (i < stages.length - 1) {
        // Not the final stage - calculate progress toward next benchmark
        const currentStageBenchmark = benchmarks[i];
        const nextStageBenchmark = benchmarks[i + 1];
        const stageRange = nextStageBenchmark - currentStageBenchmark;
        const progressInStage = currentGDD - currentStageBenchmark;

        if (stageRange > 0) {
          // Progress within current stage (0 to 1)
          const stageProgressRatio = Math.min(progressInStage / stageRange, 1);
          // Overall progress is the previous stages completed + progress in current stage
          stageProgress = (i + stageProgressRatio) / (stages.length - 1);
        } else {
          // Fallback to simple stage completion
          stageProgress = i / (stages.length - 1);
        }
      } else {
        // Final stage - 100% complete
        stageProgress = 1;
      }

      return {
        stage: stages[i],
        index: i,
        progress: Math.min(stageProgress, 1), // Ensure progress doesn't exceed 1
        nextBenchmark: benchmarks[i + 1] || benchmarks[benchmarks.length - 1],
        currentGDD: currentGDD,
        currentStageBenchmark: benchmarks[i],
      };
    }
  }

  // If GDD is below the first benchmark, we're in the first stage with minimal progress
  const firstStageRange = benchmarks[1] - benchmarks[0];
  const progressInFirstStage = firstStageRange > 0 ? Math.min(currentGDD / firstStageRange, 1) : 0;

  return {
    stage: stages[0],
    index: 0,
    progress: progressInFirstStage / (stages.length - 1), // Scale to overall progress
    nextBenchmark: benchmarks[1],
    currentGDD: currentGDD,
    currentStageBenchmark: benchmarks[0],
  };
};
